import { useMemo, useState, type ReactElement } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import { paginateRows } from '../../domain/contracts';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { EntityActivitySection } from '../activity/EntityActivitySection';
import { FilterTiles } from './FilterTiles';
import { MasterRecordPanel } from './MasterRecordPanel';
import { MasterSearchField } from './searchField';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface MasterWellRow {
  id: string;
  apiNumber: string;
  apiDisplay: string;
  name: string;
  wellType: string;
  wellStatus: string;
  operatingRegion: string;
  identifiers: { scheme: string; value: string }[];
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'api', label: 'API', header: 'API' },
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'type', label: 'Type', header: 'Type' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'ids', label: 'Ids', header: 'Ids' }
];

function groupValue(row: MasterWellRow, columnId: string): string {
  switch (columnId) {
    case 'api':
      return row.apiDisplay || row.apiNumber;
    case 'name':
      return row.name;
    case 'type':
      return row.wellType;
    case 'status':
      return row.wellStatus;
    case 'region':
      return row.operatingRegion;
    case 'ids':
      return row.identifiers.map((i) => `${i.scheme}:${i.value}`).join(', ') || '—';
    default:
      return '—';
  }
}

export function MasterWellDetail({
  item,
  variant = 'panel',
  onClose,
  onBack,
  authFetch
}: {
  item: MasterWellRow;
  variant?: 'panel' | 'page';
  onClose?: () => void;
  onBack?: () => void;
  authFetch?: AuthFetch;
}): ReactElement {
  return (
    <MasterRecordPanel
      testId="master-well-side-panel"
      ariaLabel="Well details"
      title={
        <>
          <span className="mono-id">{item.apiDisplay || item.apiNumber}</span> {item.name}
        </>
      }
      status={item.wellStatus}
      variant={variant}
      onClose={onClose}
      onBack={onBack}
    >
      <dl className="detail-list">
        <dt>API</dt>
        <dd className="mono-id">{item.apiDisplay || item.apiNumber}</dd>
        <dt>Name</dt>
        <dd>{item.name}</dd>
        <dt>Type</dt>
        <dd>{item.wellType}</dd>
        <dt>Status</dt>
        <dd>
          <span className="status-pill">{item.wellStatus}</span>
        </dd>
        <dt>Region</dt>
        <dd>{item.operatingRegion}</dd>
        <dt>Ids</dt>
        <dd>{item.identifiers.map((i) => `${i.scheme}:${i.value}`).join(', ') || '—'}</dd>
      </dl>
      {authFetch && (
        <EntityActivitySection
          authFetch={authFetch}
          path={`/wells/${item.id}/activity`}
          testId="master-well-activity"
        />
      )}
    </MasterRecordPanel>
  );
}

export function MasterWellsRegister({
  rows,
  query,
  onQuery,
  onOpen,
  authFetch
}: {
  rows: MasterWellRow[];
  query: string;
  onQuery: (value: string) => void;
  onOpen?: (id: string) => void;
  authFetch?: AuthFetch;
}): ReactElement {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);

  const statuses = useMemo(() => [...new Set(rows.map((r) => r.wellStatus).filter(Boolean))], [rows]);
  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.wellStatus === filter)),
    [filter, rows]
  );
  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <FilterTiles
        testId="master-well-tiles"
        value={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
          setSelectedId(null);
        }}
        options={[
          { id: 'all', label: 'All', count: rows.length },
          ...statuses.map((status) => ({
            id: status,
            label: status,
            count: rows.filter((r) => r.wellStatus === status).length
          }))
        ]}
      />
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <section className="card requests-grid-card" data-testid="master-wells-grid">
          <div className="row spread requests-grid-toolbar">
            <h2>Wells</h2>
          </div>
          <p className="hint">Click a row for the side panel; double-click to open the full page.</p>
          <MasterSearchField value={query} onChange={onQuery} />
          <GroupedDataGrid<MasterWellRow>
            columns={COLUMNS}
            rows={pageRows}
            getRowId={(r) => r.id}
            getGroupValue={groupValue}
            colSpan={6}
            emptyMessage="No wells match this status."
            groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
            renderRow={(row) => (
              <tr
                className={selectedId === row.id ? 'selected' : undefined}
                data-testid="master-well-row"
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  onOpen?.(row.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(row.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <td className="mono-id">{row.apiDisplay || row.apiNumber}</td>
                <td>{row.name}</td>
                <td>{row.wellType}</td>
                <td>
                  <span className="status-pill">{row.wellStatus}</span>
                </td>
                <td>{row.operatingRegion}</td>
                <td>{row.identifiers.map((i) => `${i.scheme}:${i.value}`).join(', ') || '—'}</td>
              </tr>
            )}
          />
          <GridPaginationBar
            idPrefix="master-wells"
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </section>
        {selected && <MasterWellDetail item={selected} authFetch={authFetch} onClose={() => setSelectedId(null)} />}
      </div>
    </>
  );
}
